import { useEffect, useState } from "react";
import { ChartBar, ArrowsClockwise } from "@phosphor-icons/react";
import client from "../lib/api";

/**
 * Lane pricing intelligence — "What's the market rate for FL→NY?"
 *
 * Fetches the user's OWN 30-day $/mile history for a specific corridor.
 * Renders avg / P25 / P75 side-by-side so pilots see if a fresh posting is
 * under- or over-market vs their own recent lanes.
 *
 * Hides itself when both origin and destination are empty (nothing to reference).
 * Shows a "not enough data" hint when sample size < 3.
 */
export default function LanePricingCard({ originState, destState, equipment }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let alive = true;
    if (!originState || !destState) { setData(null); return; }

    setLoading(true);
    const params = new URLSearchParams({
      origin_state: originState,
      destination_state: destState,
      days: "30",
    });
    if (equipment) params.append("equipment", equipment);

    client.get(`/loadboard/lane-pricing?${params.toString()}`)
      .then((r) => alive && setData(r.data))
      .catch(() => alive && setData(null))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [originState, destState, equipment]);

  // Nothing to show until origin + destination both selected
  if (!originState || !destState) return null;
  if (loading) {
    return (
      <div className="card-surface p-4" data-testid="lane-pricing-loading">
        <div className="flex items-center gap-2 text-neutral-500 text-xs">
          <ArrowsClockwise size={12} className="animate-spin" />
          Checking market rates for {originState} → {destState}…
        </div>
      </div>
    );
  }
  if (!data) return null;

  const n = data.sample_size || 0;

  if (n < 3) {
    return (
      <div className="card-surface p-4 border-dashed border border-white/10" data-testid="lane-pricing-empty">
        <div className="flex items-center gap-2 mb-1">
          <ChartBar size={14} className="text-neutral-500" />
          <div className="overline text-neutral-500">MARKET RATE · {originState} → {destState}</div>
        </div>
        <div className="text-xs text-neutral-500 leading-relaxed">
          Not enough bid history yet ({n} bid{n === 1 ? "" : "s"} in last 30 days).
          Reference rates will unlock once you have <b className="text-neutral-300">3+ bids</b> on this corridor.
        </div>
      </div>
    );
  }

  return (
    <div className="card-surface p-4" data-testid="lane-pricing-card">
      <div className="flex items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-2">
          <ChartBar size={14} weight="fill" className="text-[#FF5A1F]" />
          <div className="overline text-[#FF5A1F]">
            YOUR 30-DAY MARKET · {originState} → {destState}
            {data.equipment && <span className="text-neutral-500"> · {data.equipment}</span>}
          </div>
        </div>
        <div className="text-[10px] text-neutral-500">
          Based on <b className="text-neutral-300">{n}</b> bid{n === 1 ? "" : "s"}
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3" data-testid="lane-pricing-metrics">
        <PriceMetric
          label="P25 (low)"
          value={data.posted?.p25}
          tone="text-amber-400"
          hint="Bottom quartile"
        />
        <PriceMetric
          label="Avg posted"
          value={data.posted?.avg}
          tone="text-white"
          highlight
          hint="Mid-market"
        />
        <PriceMetric
          label="P75 (high)"
          value={data.posted?.p75}
          tone="text-emerald-400"
          hint="Top quartile"
        />
      </div>

      {data.won_count >= 2 && data.won?.avg && (
        <div className="mt-3 pt-3 border-t border-white/5 flex items-center justify-between text-xs text-neutral-400">
          <span>You've won <b className="text-white">{data.won_count}</b> on this lane at avg</span>
          <span className="font-mono text-emerald-400" data-testid="lane-pricing-won-avg">
            ${data.won.avg.toFixed(2)}/mi
          </span>
        </div>
      )}
    </div>
  );
}

function PriceMetric({ label, value, tone = "text-white", highlight = false, hint }) {
  const display = value != null ? `$${Number(value).toFixed(2)}` : "—";
  return (
    <div className={`rounded-lg ${highlight ? "bg-white/[0.03] border border-white/10" : ""} px-3 py-2`}>
      <div className="text-[9px] uppercase tracking-widest text-neutral-500">{label}</div>
      <div className={`font-display text-lg font-semibold mt-1 tabular-nums ${tone}`}>
        {display}<span className="text-neutral-600 text-xs font-mono ml-0.5">/mi</span>
      </div>
      {hint && <div className="text-[9px] text-neutral-600 mt-0.5">{hint}</div>}
    </div>
  );
}
